import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSessionStorage from 'src/common/hooks/useSessionStorage';
import { useRouter } from 'next/router';
import { sessionStore } from 'src/common/utils/storageWrapper';
import type { PageId, WizardFlowContext } from './types';

const initialContext: WizardFlowContext<any> = {
  flow: [],
  active: null,
  data: {},
  next() {},
  back() {},
  backTo() {},
  reset() {},
  clearSession() {},
};
const Context = createContext<WizardFlowContext<any>>(initialContext);

const FLOW_STORE = 'wizard-flow';
const DATA_STORE = 'wizard-data';

type WizardFlowProviderProps = {
  /** Providing a sessionKey value enables the session storage feature of WizardFlow.
   *  If it's not provided, no state is saved */
  sessionKey?: string | null;
  /** Scrolls to top of page when using next or back  */
  resetScrollOnPage?: boolean;
  /**
   * Use browser navigation, support back/forward on native browser controls.
   * Adds a hash to the URL with the current flow page id
   */
  browserNavigation?: boolean;
  children: ReactNode;
  /** ID of first page to show. This is optional. Can match with null */
  startPage?: string | null;
};

export const WizardFlowProvider: FC<WizardFlowProviderProps> = <T extends PageId = PageId>({
  sessionKey = null,
  browserNavigation = false,
  resetScrollOnPage = false,
  children,
  startPage = null,
}) => {
  const router = useRouter();
  const firstDraw = useRef(true);
  const flowSessionKey = sessionKey
    ? `${sessionKey}_${FLOW_STORE}`
    : null;
  const dataSessionKey = sessionKey
    ? `${sessionKey}_${DATA_STORE}`
    : null;

  const [flowStore, setFlowStore] = useSessionStorage(
    flowSessionKey,
    initialContext.flow,
  );
  const [dataStore, setDataStore] = useSessionStorage(
    dataSessionKey,
    initialContext.data,
  );

  // Don't set these to the session values on init otherwise
  // server/client HTML differs
  const [flow, setFlowState] = useState<T[]>([]);
  const [data, setDataState] = useState<Record<string, any>>({});

  /** SSR rendering clash means this can only be set in a useEffect */
  const [startId, setStartId] = useState(null);
  useEffect(() => {
    setStartId(startPage);
  }, [startPage]);

  const attemptedPage = useMemo(() => {
    // Only try follow URL hash in the first render, otherwise clicking
    // back will momentarily render the wrong page
    if (browserNavigation && firstDraw.current) {
      const hash = router.asPath.split('#')?.[1] as T;
      firstDraw.current = false;
      return hash ? hash as T : null;
    }
    return flow.length ? flow[flow.length - 1] : null;
  }, [flow, browserNavigation, router.asPath]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Session store should only be resolved after first render
  // otherwise the client DOM mismatches the server DOM
  useEffect(() => {
    setFlowState(flowStore as T[]);
    setDataState(dataStore);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFlow = useCallback((value: T[]) => {
    setFlowState(value);
    if (sessionKey) {
      setFlowStore(value);
    }
  }, [sessionKey, setFlowStore]);

  const setData = useCallback((value: Record<string, any>) => {
    setDataState(value);
    if (sessionKey) {
      setDataStore(value);
    }
  }, [sessionKey, setDataStore]);

  const matchPage = useCallback((target: T | null) => (
    (flowPage: T) => (
      target?.toLocaleLowerCase() === flowPage?.toLocaleLowerCase()
    )
  ), []);

  const active = useMemo(() => {
    const result = flow.find(matchPage(attemptedPage));
    return result ?? startId;
  }, [flow, matchPage, attemptedPage, startId]);

  const next = useCallback((page: T, options = {}) => {
    // Keep history of flow as much as possible, but reset it when
    // Moving to next screen
    const index = flow.findIndex(matchPage(active));
    const nextFlow = flow.slice(0, index + 1);
    setFlow([...nextFlow, page]);
    setData(options);

    if (browserNavigation) {
      router.push(`${router.pathname}/#${page}`);
    }

    if (resetScrollOnPage) {
      scrollToTop();
    }
  }, [
    active,
    browserNavigation,
    matchPage,
    flow,
    resetScrollOnPage,
    router,
    scrollToTop,
    setData,
    setFlow,
  ]);

  const backTo = useCallback((page: T) => {
    const index = page
      // Add 1 to keep the found page included
      ? flow.findLastIndex(matchPage(page)) + 1
      : 0;

    const nextFlow = flow.slice(0, index);
    setFlow(nextFlow);
    setData({});

    if (browserNavigation) {
      // NextJS router does not support going back multiple times (unlike browser history)
      // i.e. it's not possible to do router.back(2) or router.go(-2)
      // This instead just replaces the current page
      const nextPage = nextFlow.length ? nextFlow.slice(-1)[0] : '';
      router.replace(`${router.pathname}/#${nextPage}`);
    }
    if (resetScrollOnPage) {
      scrollToTop();
    }
  }, [
    flow,
    matchPage,
    resetScrollOnPage,
    browserNavigation,
    router,
    scrollToTop,
    setData,
    setFlow,
  ]);

  const back = useCallback(() => {
    if (flow.length > 0) {
      setFlow(flow.slice(0, flow.length - 1));
    } else {
      setFlow([]);
    }
    setData({});

    if (browserNavigation) {
      router.back();
    }
    if (resetScrollOnPage) {
      scrollToTop();
    }
  }, [
    browserNavigation,
    flow,
    resetScrollOnPage,
    router,
    scrollToTop,
    setData,
    setFlow,
  ]);

  const clearSession = useCallback(() => {
    if (flowSessionKey) {
      sessionStore.removeItem(flowSessionKey);
      sessionStore.removeItem(dataSessionKey!);
    }
  }, [dataSessionKey, flowSessionKey]);

  const [nextAfterReset, setNextAfterReset] = useState<T | null>(null);

  useEffect(() => {
    // Go to next page after first resetting form. In useEffect to prevent render clashes
    if (nextAfterReset) {
      next(nextAfterReset);
      setNextAfterReset(null);
    }
  }, [nextAfterReset, next]);

  const reset = useCallback((page?: T) => {
    setFlow(page ? [page] : []);
    setData({});
    if (page) {
      setNextAfterReset(page);
    }
  }, [setData, setFlow]);

  const value = useMemo(() => ({
    flow,
    active,
    data,
    next,
    back,
    backTo,
    reset,
    clearSession,
  }), [
    flow,
    active,
    data,
    next,
    back,
    backTo,
    reset,
    clearSession,
  ]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

const useWizardFlow = <T extends PageId = PageId>(): WizardFlowContext<T> => {
  const context = useContext<WizardFlowContext<T>>(Context);

  return context;
};

export default useWizardFlow;
